import { useCallback, useRef } from 'react';
import { PlayerStatuses, mediaPlaybackActions } from '../../stores/AudioPlaybackStore';
import { toAudioPrecision, tryUseMono } from '../../../utils/mediaPlayerUtils';
import { VOIP } from '../../../types/CallSourceTypes';
export const usePlaybackControls = ({
  clipEndTime,
  clipStartTime,
  containInClipTime,
  handleSetPlayerHeadLocation,
  mediaRef,
  mediaStateDispatch,
  preciseDuration,
  source
}) => {
  const isMonoRef = useRef(false);
  return {
    handleForward: useCallback(time => {
      if (!mediaRef.current) return;
      if (containInClipTime && clipEndTime) {
        handleSetPlayerHeadLocation(Math.min(mediaRef.current.currentTime + time, clipEndTime));
      } else {
        handleSetPlayerHeadLocation(Math.min(mediaRef.current.currentTime + time, preciseDuration));
      }
    }, [clipEndTime, containInClipTime, handleSetPlayerHeadLocation, mediaRef, preciseDuration]),
    handlePause: useCallback(() => {
      if (mediaRef.current) {
        mediaRef.current.pause();
        mediaStateDispatch(mediaPlaybackActions.setStatus(PlayerStatuses.PAUSED));
      }
    }, [mediaStateDispatch, mediaRef]),
    handlePlay: useCallback(() => {
      if (!mediaRef.current) return;
      if (source === VOIP && !isMonoRef.current) {
        tryUseMono(mediaRef.current);
        isMonoRef.current = true;
      }
      if (mediaRef.current.paused || mediaRef.current.ended) {
        // If containInClipTime is set to true and player is at the end of the clip already,
        // we want to start playing from the start of the clip when user unpauses.
        if (clipEndTime && containInClipTime && mediaRef.current.currentTime >= clipEndTime) {
          handleSetPlayerHeadLocation(clipStartTime || 0);
        }
        mediaStateDispatch(mediaPlaybackActions.setPlaybackRequested(true));
        mediaRef.current.play().catch(() => {});
      }
    }, [clipEndTime, clipStartTime, containInClipTime, handleSetPlayerHeadLocation, mediaRef, mediaStateDispatch, source]),
    handlePlayerHeadUpdate: useCallback(time => {
      let audioTime = toAudioPrecision(time, preciseDuration);
      audioTime = Math.max(audioTime, 0);
      audioTime = Math.min(audioTime, preciseDuration);
      if (containInClipTime && clipStartTime) {
        audioTime = Math.max(audioTime, clipStartTime);
      }
      if (containInClipTime && clipEndTime) {
        audioTime = Math.min(audioTime, clipEndTime);
      }
      handleSetPlayerHeadLocation(audioTime);
    }, [clipEndTime, clipStartTime, containInClipTime, handleSetPlayerHeadLocation, preciseDuration]),
    handleRewind: useCallback(time => {
      if (!mediaRef.current) return;
      if (containInClipTime && clipStartTime) {
        handleSetPlayerHeadLocation(Math.max(mediaRef.current.currentTime - time, clipStartTime));
      } else {
        handleSetPlayerHeadLocation(Math.max(mediaRef.current.currentTime - time, 0));
      }
    }, [clipStartTime, containInClipTime, handleSetPlayerHeadLocation, mediaRef])
  };
};